/**
 * Il protocollo degli intenti di verifica: forme, impronte, percorsi.
 *
 * **Un verdetto non e' una chiamata: e' un oggetto che sopravvive alla rete.**
 * L'operatore decide e la pagina va avanti — non puo' fare altrimenti, perche'
 * mettere il viaggio di andata e ritorno dentro il tempo di lavoro, su
 * centinaia di candidati al giorno, e' il costo della verifica. La consegna
 * arriva dopo: magari da un'altra scheda, magari quando la rete torna.
 *
 * **La forma canonica e' la parte che, sbagliata, non si vede.** Se la pagina
 * ordinasse le chiavi in un modo e il servizio in un altro, ogni rinvio
 * sembrerebbe una decisione diversa: la chiave di idempotenza non
 * riconoscerebbe piu' nulla, e il doppione arriverebbe proprio nel momento in
 * cui le chiavi esistono per impedirlo. Le due parti devono ottenere **la
 * stessa stringa** — questa e' il gemello di `canonical` in
 * `gridfly.domain.review_intents`.
 *
 * **Due gesti, non uno.** Registrare dichiara la decisione e prenota il
 * candidato; applicare la scrive e consuma il lease. Separarli e' cio' che
 * permette al servizio di rispondere «l'avevo gia' presa» a chi ha perso la
 * risposta, invece di prendere due volte la stessa decisione.
 */
import { ServiceError, ask, get } from './api'

/**
 * Che cosa un intento puo' essere. Il ritiro non porta contenuto: e' proprio
 * cio' che sta togliendo.
 *
 * I tre generi della copertura (GI-169) passano da **qui** e non da una
 * seconda casella d'uscita: una macchina a stati per il banco e una per la
 * copertura sarebbero due riconciliazioni e due depositi che divergono al primo
 * difetto corretto in una sola delle due.
 */
export type IntentKind =
  | 'verdict'
  | 'reversal'
  | 'manual_finding'
  | 'photo_reviewed'
  | 'not_inspected'

/** I tre generi che parlano di copertura invece che di un candidato. */
export const COVERAGE_KINDS: IntentKind[] = [
  'manual_finding',
  'photo_reviewed',
  'not_inspected',
]

export function isCoverage(kind: string): boolean {
  return (COVERAGE_KINDS as string[]).includes(kind)
}

/**
 * Dove si trova un intento **dal punto di vista della pagina**.
 *
 * Non coincide con lo stato del servizio, e la differenza e' il motivo per cui
 * questo tipo esiste: `local` e' cio' che il servizio non ha mai visto, e non
 * ha un corrispondente di la'. Gli altri quattro sono cio' che il servizio ha
 * risposto, riportato senza reinterpretarlo.
 */
export type IntentState = 'local' | 'decision_pending' | 'applied' | 'rejected' | 'stale'

/**
 * Da dove viene un intento.
 *
 * `session` e' l'unica strada che passa per il registro: porta lease, era e
 * versione attesa, e il servizio puo' verificarla. `direct` e' la vecchia
 * strada senza sessione, che il servizio ora rifiuta sui candidati riservati
 * da altri. `legacy` sono le righe adottate dal deposito precedente: prive di
 * lease ed era, sono visibili ma non consegnabili.
 */
export type IntentOrigin = 'session' | 'direct' | 'legacy'

/** Una decisione presa, con tutto cio' che serve per consegnarla piu' tardi. */
export interface ReviewIntent {
  /** Nasce con la decisione, non con l'invio: e' cio' che rende sicuro il
   *  rinvio dopo una risposta perduta. */
  idempotencyKey: string
  sessionId: string
  /**
   * Il candidato del modello, dove la decisione parla di uno.
   *
   * Nullo per le tre dichiarazioni di copertura: quelle parlano di un
   * fotogramma o di un elemento della rete. Il soggetto arriva per nome — tre
   * campi e non un identificativo generico — perche' dedurlo dal genere in
   * dieci punti significa sbagliarlo in uno.
   */
  detectionId: string | null
  photoId?: string | null
  elementId?: string | null
  /** Assente per un ritiro: il verdetto che sta annullando lo ha consumato. */
  leaseToken: string | null
  reviewEpoch: number
  /** Su quale stato del candidato si stava decidendo. */
  expectedCandidateVersion: number
  kind: IntentKind
  payload: Record<string, unknown>
  state: IntentState
  createdAt: string
  /** L'ora del servente, non quella della macchina. */
  undoDeadline: string | null
  origin?: IntentOrigin
  originalKey?: string | null
  reviewId?: string | null
  /** La versione del candidato dopo l'applicazione: e' quella che un ritiro
   *  deve dichiarare di attendersi. */
  candidateVersion?: number | null
  /** La frase da mostrare quando qualcosa non e' passato. */
  note?: string | null
  /** Il codice con cui il servizio ha detto no. Separato dalla frase perche'
   *  la schermata decide sul codice e l'operatore legge la frase: tenerne uno
   *  solo obbligherebbe una delle due a interpretare l'altra. */
  rejectionCode?: string | null
  attempts?: number
}

/** L'intento come lo vede il servizio. */
export interface RegisteredIntent {
  idempotency_key: string
  review_session_id: string
  detection_id: string | null
  photo_id?: string | null
  element_id?: string | null
  kind: string
  state: string
  lease_token: string | null
  lease_state: string | null
  review_epoch: number
  expected_candidate_version: number
  payload_hash: string
  created_at: string
  applied_at: string | null
  undo_deadline: string | null
  review_id: string | null
  rejection_code: string | null
  candidate_version: number
}

/** Che ne e' stato di una chiave. `unknown` non e' un errore: e' la riga che
 *  il servizio non ha mai visto, e va registrata, non data per persa. */
export interface ReconciledIntent {
  idempotency_key: string
  status: 'applied' | 'decision_pending' | 'rejected' | 'unknown' | 'stale'
  detection_id: string | null
  photo_id?: string | null
  element_id?: string | null
  kind: string | null
  review_id: string | null
  applied_at: string | null
  undo_deadline: string | null
  rejection_code: string | null
  candidate_version: number | null
}

/**
 * Come si nomina il soggetto di una decisione, in una chiave sola.
 *
 * Un nome solo perche' il deposito deve poter dire «l'ultima decisione su
 * *questo*» senza sapere di che genere sia: tre mappe separate — per candidato,
 * per fotogramma, per elemento — vorrebbero dire tre letture e tre allineamenti
 * a mano, e la schermata che ne legge due su tre mostrerebbe uno stato vecchio
 * proprio dove qualcuno ha appena deciso.
 *
 * Il prefisso c'e' perche' gli identificativi vengono da tabelle diverse:
 * senza, un fotogramma e un elemento con la stessa identita' sarebbero lo
 * stesso soggetto.
 */
export function subjectOf(intent: {
  detectionId?: string | null
  photoId?: string | null
  elementId?: string | null
  kind: string
}): string {
  if (intent.kind === 'manual_finding') {
    return `manual:${intent.photoId ?? ''}:${intent.elementId ?? ''}`
  }
  if (intent.kind === 'photo_reviewed') return `photo:${intent.photoId ?? ''}`
  if (intent.kind === 'not_inspected') return `element:${intent.elementId ?? ''}`
  return `detection:${intent.detectionId ?? ''}`
}

/** L'identita' del candidato dentro una chiave di soggetto, se ce n'e' una. */
export function detectionIn(subject: string): string | null {
  return subject.startsWith('detection:') ? subject.slice('detection:'.length) : null
}

/**
 * La forma canonica del contenuto di una decisione.
 *
 * Chiavi ordinate a ogni livello, nessuno spazio. `JSON.stringify` da solo non
 * basta: conserva l'ordine di inserimento, e due schermate che compongono lo
 * stesso verdetto in ordine diverso otterrebbero due impronte diverse.
 */
export function canonical(payload: unknown): string {
  return JSON.stringify(sorted(payload))
}

function sorted(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sorted)
  if (value === null || typeof value !== 'object') return value
  const source = value as Record<string, unknown>
  const target: Record<string, unknown> = {}
  for (const key of Object.keys(source).sort()) target[key] = sorted(source[key])
  return target
}

/** L'impronta della forma canonica, in esadecimale minuscolo come la scrive
 *  Python. */
export async function payloadHash(payload: Record<string, unknown>): Promise<string> {
  const bytes = new TextEncoder().encode(canonical(payload))
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest))
    .map((one) => one.toString(16).padStart(2, '0'))
    .join('')
}

/** Dichiara la decisione e prenota il candidato.
 *
 * L'impronta viaggia con la richiesta: e' il solo modo di accorgersi che le due
 * forme canoniche hanno divergito, e di accorgersene al primo verdetto invece
 * che a fine turno. */
export async function registerIntent(intent: ReviewIntent): Promise<RegisteredIntent> {
  return ask<RegisteredIntent>(
    `/review-sessions/${encodeURIComponent(intent.sessionId)}/intents`,
    {
      idempotency_key: intent.idempotencyKey,
      detection_id: intent.detectionId,
      photo_id: intent.photoId ?? null,
      element_id: intent.elementId ?? null,
      lease_token: intent.leaseToken,
      review_epoch: intent.reviewEpoch,
      expected_candidate_version: intent.expectedCandidateVersion,
      kind: intent.kind,
      payload: intent.payload,
      payload_hash: await payloadHash(intent.payload),
      original_intent_key: intent.originalKey ?? null,
    },
  )
}

/** Scrive la decisione gia' dichiarata.
 *
 * Senza il contenuto: e' gia' registrato, e rimandarlo aprirebbe la porta a
 * un'applicazione che scrive qualcosa di diverso da cio' che era stato
 * promesso. */
export function applyIntent(sessionId: string, key: string): Promise<RegisteredIntent> {
  return ask<RegisteredIntent>(
    `/review-sessions/${encodeURIComponent(sessionId)}/intents/${encodeURIComponent(key)}/apply`,
    {},
  )
}

/** Chiede che fine hanno fatto queste chiavi. Non applica niente. */
export async function reconcileIntents(
  sessionId: string,
  keys: string[],
): Promise<ReconciledIntent[]> {
  // Senza chiavi non c'e' niente da chiedere: una richiesta vuota sarebbe un
  // viaggio per ottenere una lista vuota, e ne parte una a ogni ripresa.
  if (keys.length === 0) return []
  const query = new URLSearchParams()
  for (const key of keys) query.append('keys', key)
  const answer = await get<{ intents: ReconciledIntent[] }>(
    `/review-sessions/${encodeURIComponent(sessionId)}/reconciliation?${query.toString()}`,
  )
  return answer.intents
}

/** I codici che dicono «il mondo e' andato avanti»: il lavoro non e' passato,
 *  ma la decisione in se' non era sbagliata — si riprende il candidato e si
 *  decide di nuovo. */
const STALE_CODES = new Set([
  'intent_stale',
  'lease_lost',
  'lease_not_yours',
  'lease_candidate_mismatch',
  'stale_epoch',
  'session_expired',
  'undo_deadline_elapsed',
  'nothing_to_reverse',
])

export function isStaleWork(error: unknown): boolean {
  return error instanceof ServiceError && STALE_CODES.has(error.code)
}

/**
 * Se il servizio ha detto no in modo definitivo.
 *
 * Un 4xx che non sia lavoro obsoleto non guarisce ripetendolo: insistere
 * bloccherebbe la coda e nasconderebbe all'operatore che quel verdetto non e'
 * stato registrato. I 5xx e i guasti di rete no: quelli passano.
 */
export function isRejection(error: unknown): boolean {
  if (!(error instanceof ServiceError)) return false
  return error.status >= 400 && error.status < 500 && !isStaleWork(error)
}
